import * as React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'

// Initialize development services
import { devLogging } from './modules/shared/services/devLoggingService'
import { 
  envWatcher, 
  serverHealthMonitor, 
  buildErrorDetector 
} from './modules/shared/services/devEnvironmentWatcher'

// Initialize dev logging immediately
devLogging.info('APP_BOOT', 'Development services initializing', {
  environment: import.meta.env.MODE,
  isDev: import.meta.env.DEV,
});

// Start environment watcher
envWatcher.start();

// Start server health monitoring in development
if (import.meta.env.DEV) {
  serverHealthMonitor.start();
}

// Expose dev tools globally
if (import.meta.env.DEV) {
  window.__devTools = {
    envWatcher,
    serverHealthMonitor,
    buildErrorDetector,
    startHealthCheck: () => serverHealthMonitor.start(),
    stopHealthCheck: () => serverHealthMonitor.stop(),
  };
  
  devLogging.info('APP_BOOT', 'Dev tools exposed at window.__devTools');
}

devLogging.info('APP_BOOT', 'Development services initialized successfully');

// ── Global unhandled error handlers ─────────────────────────────────────────
// These catch errors that React Error Boundaries cannot:
//   - async/await rejections
//   - errors in setTimeout / requestAnimationFrame
//   - errors in event handlers that aren't wrapped in try/catch

window.addEventListener('error', (event: ErrorEvent) => {
  if (typeof event.message === 'string' && event.message.includes('Cannot access stylesheet')) {
    return;
  }
  console.error('[Global] Uncaught error:', event.error ?? event.message);
  reportGlobalError(event.error, event.filename, event.lineno);
});

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const rejectionMessage = String(
    event.reason instanceof Error ? event.reason.message : event.reason ?? '',
  );
  if (rejectionMessage.includes('Cannot access stylesheet')) {
    return;
  }
  if (isStaleChunkError(rejectionMessage)) {
    event.preventDefault();
    reloadOnceForStaleChunk();
    return;
  }
  console.error('[Global] Unhandled promise rejection:', event.reason);
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  reportGlobalError(error);
});

// A deploy replaces hashed asset filenames and prunes old releases. A tab left
// open across a deploy still holds the old entry bundle in memory, so a later
// lazy `import()` (route navigation) requests a chunk URL that no longer
// exists on the server. The fix is simply to reload — the fresh index.html
// points at the current bundle — guarded so a genuinely broken chunk doesn't
// reload-loop forever.
function isStaleChunkError(message: string): boolean {
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
}

window.addEventListener('vite:preloadError', () => {
  reloadOnceForStaleChunk();
});

function reloadOnceForStaleChunk() {
  const key = 'epprem:stale-chunk-reload-at';
  const lastReload = Number(sessionStorage.getItem(key) ?? 0);
  if (Date.now() - lastReload < 10_000) {
    // Already reloaded for this within the last 10s — avoid a loop.
    return;
  }
  sessionStorage.setItem(key, String(Date.now()));
  window.location.reload();
}

function reportGlobalError(error?: Error | null, source?: string, lineno?: number) {
  try {
    const payload = {
      message: error?.message ?? 'Unknown error',
      stack: error?.stack ?? null,
      source: source ?? null,
      lineno: lineno ?? null,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(
        '/api/logs/frontend-error',
        new Blob([JSON.stringify(payload)], { type: 'application/json' }),
      );
    }
  } catch {
    // Swallow — global error reporter must never throw
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
